// Body analytics — config + extractor. The WEAKEST-fit inherit (reported):
// body_measurements are IRREGULAR (not daily-cadence), so:
//   - reducer = avg (you never "sum" body weight; a gap is "no data");
//   - NO streak (a measurement streak is meaningless);
//   - the existing EvolutionChart already shows the multi-series SHAPE — this
//     dashboard adds what it lacks: period-over-period Δ and a projection.
// So it inherits, but as a specialization, not the daily-cadence pattern.
//
// GUARDRAIL (Body = no judgment): declares only averages/trend. The dashboard's
// trend arrow is valence-neutral (up and down both render cyan, never red/green
// "good/bad"), matching the page's existing non-judgmental DeltaBadge. No
// "ideal/overweight" framing anywhere.

import type { Sample } from "./engine";
import type { DashboardConfig } from "./dashboard";
import type { BodyMeasurement } from "@/hooks/useBodyMeasurements";

export const BODY_DASHBOARD: DashboardConfig = {
  moduleKey: "body",
  titleKey: "body.analytics.title",
  defaultGranularity: "month",
  metrics: [
    {
      key: "weight",
      labelKey: "body.analytics.metric.weight",
      reducer: "avg",
      color: "hsl(187 72% 50%)",
      unitKey: "analytics.unit.kg",
      chart: "line",
      projectable: true,
      format: (v) => v.toFixed(1),
    },
    {
      key: "body_fat",
      labelKey: "body.analytics.metric.bodyFat",
      reducer: "avg",
      color: "hsl(30 90% 55%)",
      unitKey: "analytics.unit.pct",
      chart: "line",
      projectable: true,
      format: (v) => v.toFixed(1),
    },
    {
      key: "waist",
      labelKey: "body.analytics.metric.waist",
      reducer: "avg",
      color: "hsl(265 60% 65%)",
      unitKey: "analytics.unit.cm",
      chart: "line",
      projectable: true,
      format: (v) => v.toFixed(1),
    },
  ],
};

export function extractBodySamples(rows: BodyMeasurement[]): Record<string, Sample[]> {
  const weight: Sample[] = [];
  const body_fat: Sample[] = [];
  const waist: Sample[] = [];

  for (const r of rows) {
    if (!r.measured_at) continue;
    const t = new Date(r.measured_at).getTime();
    if (!Number.isFinite(t)) continue;
    const category = r.source ?? undefined;

    if (typeof r.weight_kg === "number") weight.push({ t, v: r.weight_kg, category });
    if (typeof r.body_fat_pct === "number") body_fat.push({ t, v: r.body_fat_pct, category });
    if (typeof r.waist_cm === "number") waist.push({ t, v: r.waist_cm, category });
  }

  return { weight, body_fat, waist };
}
